/**
 * AccountRow
 *
 * Renders a single account row inside the table, including the expand/collapse
 * control, code with depth indicator, name, subtype, tags, currency, status,
 * and action buttons.
 */

import { Badge, Skeleton } from '@elsapiens/ui'
import { cn } from '@elsapiens/utils'

import { isCashOrBankSubtype } from '../../constants/labels'

import type { AccountRow as AccountRowData } from './chart-of-accounts-helpers'
import type { Account } from '../../services/account-service'

// ── Sub-components ─────────────────────────────────────────────────────────────

function ChevronIcon({ isExpanded }: { isExpanded: boolean }): JSX.Element {
  return (
    <svg
      className={cn('w-3.5 h-3.5 text-foreground-secondary transition-transform', isExpanded && 'rotate-90')}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
    </svg>
  )
}

function SpinnerIcon(): JSX.Element {
  return (
    <svg className="w-3.5 h-3.5 text-foreground-secondary animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" fill="currentColor" />
    </svg>
  )
}

// ── Cell renderers (used as column render functions) ──────────────────────────

interface CodeCellProps {
  row: AccountRowData
  onToggle: (id: string) => void
}

export function CodeCell({ row, onToggle }: CodeCellProps): JSX.Element {
  const { account, depth, isExpanded, hasChildren, isLoading } = row
  const depthIndicator = depth > 0 ? `${'-'.repeat(depth)} ` : ''
  return (
    <div className="flex items-center gap-1">
      {hasChildren ? (
        <button
          className="p-0.5 rounded hover:bg-primary/10 flex-shrink-0"
          disabled={isLoading}
          title={isExpanded ? 'Collapse' : 'Expand'}
          onClick={(e) => { e.stopPropagation(); onToggle(account.id) }}
        >
          {isLoading ? <SpinnerIcon /> : <ChevronIcon isExpanded={isExpanded} />}
        </button>
      ) : null}
      {!hasChildren && depth > 0 ? <div className="w-4" /> : null}
      <span className={cn('font-mono text-sm', hasChildren && 'font-semibold')}>
        {depthIndicator}{account.code}
      </span>
    </div>
  )
}

export function NameCell({ row }: { row: AccountRowData }): JSX.Element {
  return <span className={cn(row.hasChildren && 'font-semibold')}>{row.account.name}</span>
}

export function SubtypeCell({ row }: { row: AccountRowData }): JSX.Element {
  return (
    <span className="text-foreground-secondary flex items-center gap-1.5">
      {isCashOrBankSubtype(row.account.accountSubtype) && (
        <svg className="w-3.5 h-3.5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      )}
      {row.account.accountSubtype !== '' ? row.account.accountSubtype : '-'}
    </span>
  )
}

export function TagsCell({ row }: { row: AccountRowData }): JSX.Element {
  const { account } = row
  const hasSystemTag = !!account.systemTag
  const hasCustomTags = account.tags && account.tags.length > 0
  if (!hasSystemTag && !hasCustomTags) { return <span className="text-foreground-secondary">-</span> }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {hasSystemTag && (
        <Badge size="sm" variant="info">{account.systemTag?.replace(/_/g, ' ')}</Badge>
      )}
      {account.tags?.map(tag => (
        <Badge key={tag} size="sm" variant="secondary">{tag}</Badge>
      ))}
    </div>
  )
}

export function CurrencyCell({ row, baseCurrency }: { row: AccountRowData; baseCurrency: string }): JSX.Element {
  return <span className="text-foreground-secondary font-mono">{row.account.currency ?? baseCurrency}</span>
}

export function StatusCell({ row }: { row: AccountRowData }): JSX.Element {
  return (
    <Badge size="sm" variant={row.account.isActive ? 'success' : 'secondary'}>
      {row.account.isActive ? 'Active' : 'Inactive'}
    </Badge>
  )
}

interface ActionsCellProps {
  row: AccountRowData
  onEdit: (account: Account) => void
  onToggleActive: (account: Account) => void
  onViewLedger: (accountId: string) => void
}

export function ActionsCell({ row, onEdit, onToggleActive, onViewLedger }: ActionsCellProps): JSX.Element {
  return (
    <div className="flex items-center gap-1">
      <button
        className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900 text-foreground-secondary hover:text-blue-600"
        title="View in General Ledger"
        onClick={(e) => { e.stopPropagation(); onViewLedger(row.account.id) }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
          <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      </button>
      <button
        className="p-1.5 rounded hover:bg-primary/10 text-foreground-secondary hover:text-primary"
        title="Edit"
        onClick={(e) => { e.stopPropagation(); onEdit(row.account) }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      </button>
      {!row.account.isSystem && !row.account.code?.includes('-') && <ToggleActiveButton row={row} onToggleActive={onToggleActive} />}
    </div>
  )
}

function ToggleActiveButton({ row, onToggleActive }: { row: AccountRowData; onToggleActive: (account: Account) => void }): JSX.Element {
  return (
    <button
      className={cn(
        'p-1.5 rounded text-foreground-secondary',
        row.account.isActive
          ? 'hover:bg-red-100 dark:hover:bg-red-900 hover:text-red-600'
          : 'hover:bg-green-100 dark:hover:bg-green-900 hover:text-green-600'
      )}
      title={row.account.isActive ? 'Deactivate' : 'Reactivate'}
      onClick={(e) => { e.stopPropagation(); onToggleActive(row.account) }}
    >
      {row.account.isActive ? (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      ) : (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      )}
    </button>
  )
}

// ── Skeleton columns ────────────────────────────────────────────────────────

export const SKELETON_COLUMNS = [
  { key: 'code', header: 'Code', width: '12rem', render: () => <Skeleton className="h-5 w-12" /> },
  { key: 'name', header: 'Name', render: () => <Skeleton className="h-5 w-36" /> },
  { key: 'subtype', header: 'Subtype', render: () => <Skeleton className="h-5 w-20" /> },
  { key: 'tags', header: 'Tags', render: () => <Skeleton className="h-5 w-16" /> },
  { key: 'currency', header: 'Currency', width: '5rem', render: () => <Skeleton className="h-5 w-10" /> },
  { key: 'status', header: 'Status', width: '6rem', render: () => <Skeleton className="h-5 w-14 rounded-full" /> },
  { key: 'actions', header: 'Actions', width: '8rem', render: () => <Skeleton className="h-5 w-12" /> },
]

export const SKELETON_DATA = Array.from({ length: 4 }, (_, i) => ({ id: String(i) }))
